import { ProviderModel } from './../../models/provider.model';
import { Injectable } from '@angular/core';
//
import { BaseRepository } from './base.repository';
import { BaseEntityModel } from './base-entity.model';
import { SearchResponseModel } from '../models/search-response.model';
import { CrudOperationEnum } from '../../models/enums/crud-operation.enum';
import { DbContext } from '../utils/db-context';
import { AllocationModel } from '../../models/allocation.model';
import { IDBPTransaction } from 'idb';

/**
 * Options for write operations that may need to opt out of local change
 * tracking (e.g. writes performed while applying server data during a sync).
 */
export interface WriteOptions {
    skipLocalUpdate?: boolean;
}

/**
 * Change-tracked CRUD on top of the untracked persistence primitives in
 * {@link BaseStore}. Each write delegates to the corresponding `super.*()`
 * primitive and then records the operation in `unsyncedChanges`.
 */
@Injectable()
export abstract class BaseEntityRepository<
    T extends BaseEntityModel
> extends BaseRepository<T> {
    override async add(data: T): Promise<IDBValidKey> {
        const resp = await super.add(data);
        await this.markAsChange(CrudOperationEnum.Create, data);

        return resp;
    }

    override async edit(data: T): Promise<IDBValidKey> {
        const resp = await super.edit(data);
        await this.markAsChange(CrudOperationEnum.Update, data);

        return resp;
    }

    override async delete(id: string): Promise<undefined> {
        const resp = await super.delete(id);
        await this.markAsChange(CrudOperationEnum.Delete, undefined, id);

        return resp;
    }

    override async addRange(data: T[], options?: WriteOptions): Promise<undefined> {
        const resp = await super.addRange(data);
        await this.markAsChangeRange(
            CrudOperationEnum.Create,
            data,
            undefined,
            this.resolveSkip(options)
        );

        return resp;
    }

    override async editRange(data: T[], options?: WriteOptions): Promise<undefined> {
        let resp: undefined;

        if (this.config.tableName === DbContext.Allocations) {
            const transaction = this.database.transaction(
                this.config.tableName,
                'readwrite'
            );

            await Promise.all(
                data.map(d => this._buildAllocation(d, transaction))
            );

            resp = await transaction.done;
        } else {
            resp = await super.editRange(data);
        }

        await this.markAsChangeRange(
            CrudOperationEnum.Update,
            data,
            undefined,
            this.resolveSkip(options)
        );

        return resp;
    }

    override async deleteRange(ids: string[], options?: WriteOptions): Promise<undefined> {
        const resp = await super.deleteRange(ids);
        await this.markAsChangeRange(
            CrudOperationEnum.Delete,
            undefined,
            ids,
            this.resolveSkip(options)
        );

        return resp;
    }

    /**
     * Clears the whole table.
     *
     * By default every removed record is recorded as a Delete in
     * `unsyncedChanges` so a local purge still propagates to the server on the
     * next sync. Pass `{ skipLocalUpdate: true }` for sync-time purges (e.g. a
     * full re-download) where the wipe must NOT be tracked as user changes.
     */
    override async deleteAll(options?: WriteOptions): Promise<undefined> {
        const skip = this.resolveSkip(options);

        if (skip) {
            return super.deleteAll();
        }

        const keys = await this.database.getAllKeys(this.config.tableName);
        await super.deleteAll();
        await this.markAsChangeRange(
            CrudOperationEnum.Delete,
            undefined,
            keys.map(key => key.toString()),
            skip
        );

        return undefined;
    }

    async sync(data: SearchResponseModel<T>): Promise<void> {
        if (data.totalRecords > 0) {
            // Data coming from the server must not be tracked as local changes.
            // Passing the flag explicitly (instead of flipping a shared field)
            // means a throw here can never leave change-tracking disabled for
            // subsequent user edits.
            await this.editRange(data.records, { skipLocalUpdate: true });
        }
    }

    private resolveSkip(options?: WriteOptions): boolean {
        return options?.skipLocalUpdate ?? this.skipLocalUpdate;
    }

    private async _buildAllocation(
        data: T,
        transaction: IDBPTransaction<unknown, [string]>
    ) {
        const customerId = data['key'] as string;
        const providers: ProviderModel[] = [];
        Object.keys(data['value']).forEach(providerId => {
            providers.push(
                new ProviderModel({
                    id: providerId,
                    startDate: data['value'][providerId].startDate,
                    exitDate: data['value'][providerId].exitDate
                })
            );
        });

        return transaction.store.put(
            new AllocationModel({
                id: customerId.trim(),
                customerId: customerId.trim(),
                providers: providers
            })
        );
    }
}
